import React, {useState, useEffect} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './DataSourceDetails.css';
import postgresLogo from '../../assets/images/integrations/postgres.png';
import excelLogo from '../../assets/images/integrations/excel.png';
import quickbooksLogo from '../../assets/images/integrations/quickbooks.png';
import {ReactComponent as MoreIcon} from '../../assets/icons/more-icon.svg'
import {ReactComponent as DownIcon} from '../../assets/icons/down-icon.svg'
import {ReactComponent as RefreshIcon} from '../../assets/icons/refresh-icon.svg'
import axios from 'axios';
import _ from 'lodash';
import Popup from '../../components/Popup/Popup';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import CreateDataSource from './CreateDataSource';

const DataSourceDetails = ({dataSource, setDataSources, updateDataSource, setAlert, setModelOpen, disconnectDataSource}) => {
    const [localDataSource, setLocalDataSource] = useState(dataSource);
    const [isSaved, setIsSaved] = useState(true);
    const [moreSelectionOpen, setMoreSelectionOpen] = useState(false);
    const [titleDropdownOpen, setTitleDropdownOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addDataSourceOpen, setAddDataSourceOpen] = useState(false);
    const [syncOpen, setSyncOpen] = useState(false);
    const [resyncing, setResyncing] = useState(false);

    useEffect(() => {
        if (!_.isEqual(localDataSource, dataSource)) {
            setIsSaved(false);
        } else { 
            setIsSaved(true);
        }
    }, [localDataSource]);

    useEffect(() => {
        setLocalDataSource({...dataSource});
    }, [dataSource]);


    const getLogo = () => {
        switch (dataSource.dataSourceType) {
            case 'Postgres':
                return postgresLogo;
            case 'Excel':
                return excelLogo;
            case 'QuickBooks':
                return quickbooksLogo;
        }
    }

    const handleSaveClicked = () => {
        if (!_.isEqual(localDataSource, dataSource)) {
            // Create updates object with only changed fields
            const updates = {};
            Object.keys(localDataSource).forEach(key => {
                if (!_.isEqual(localDataSource[key], dataSource[key])) {
                    updates[key] = localDataSource[key];
                }
            });
            updateDataSource(dataSource.id, updates);
            setModelOpen(false);
        }
    }

    const handleCancelClicked = () => {
        setModelOpen(false);
    }

    const handleToggleAccessType = () => {
        if (localDataSource.accessType === 'team-wide') {
            setLocalDataSource({...localDataSource, accessType: 'private'});
        } else {
            setLocalDataSource({...localDataSource, accessType: 'team-wide'});
        }
    }

    const formatDate = (date) => {
        const options = { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        const d = new Date(date).toLocaleDateString(undefined, options);
        return d;
    };

    const formatSyncFreq = () => {
        switch (localDataSource.syncFreq) {
            case 'daily':
                return 'Daily';
            case 'weekly':
                return 'Once a week';
            case 'monthly':
                return 'Once a month';
            case 'one-time':
                return 'One Time';
            default:
                return 'One Time';
        }
    }

    const getButtonTextByStatus = (status) => {
        switch (status) {
            case 'ready':
                return dataSource.dataSourceType === 'Excel' ? 'Remove Data' : 'Disconnect';
            case 'syncing':
                return dataSource.dataSourceType === 'Excel' ? 'Please wait...' : 'Please wait...';
            case 'sync-failed':
                return dataSource.dataSourceType === 'Excel' ? 'Retry Upload' : 'Retry Sync';
            case 'disconnected':
                return dataSource.dataSourceType === 'Excel' ? 'Upload Excel' : 'Connect Source';
            case 'not-synced':
                return dataSource.dataSourceType === 'Excel' ? 'Upload Excel' : 'Begin Sync';
            default:
                return dataSource.dataSourceType === 'Excel' ? 'Upload Excel' : 'Connect Source';
        }
    }

    const handleConfirmDisconnectClicked = async () => {
        try {
            setLoading(true);
            setConfirmationOpen(false);
            await disconnectDataSource({dataSourceId: localDataSource.id, dataSourceType: localDataSource.dataSourceType});
        } catch (error) {
            console.error('Error disconnecting data source:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleConnectSourceClicked = () => {
        // bring up the connect source option for the given data source type
        setAddDataSourceOpen(true);
    }

    const handleBeginSyncClicked = () => {
        setSyncOpen(true);
    }

    const handleResyncClicked = async () => {
        const syncUpdate = { ...localDataSource, status: 'syncing' };
        try {
            setResyncing(true);
            setLocalDataSource(syncUpdate);
            setDataSources((prevDataSources) => prevDataSources.map((source) =>
                source.id === dataSource.id ? { ...source, status: 'syncing' } : source
            ));
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/resync`, {
                dataSourceId: dataSource.id
            }, { withCredentials: true });
            setAlert('Sync triggered', 'Your data source is now syncing. This may take a few minutes.', true);
        } catch (error) {
            console.error('Error triggering resync:', error);
            setLocalDataSource({...localDataSource, status: dataSource.status});
            setDataSources((prevDataSources) => prevDataSources.map((source) =>
                source.id === dataSource.id ? { ...source, status: dataSource.status } : source
            ));
            setAlert('Resync failed', error.response?.data?.error || 'An unexpected error occurred.', false);
        } finally {
            setResyncing(false);
        }
    };

    const getStatusDisplayText = (status) => {
        switch (status) {
            case 'ready':
                return 'Ready';
            case 'syncing':
                return dataSource.dataSourceType === 'Excel' ? 'Uploading' : 'Syncing';
            case 'sync-failed':
                return dataSource.dataSourceType === 'Excel' ? 'Upload Failed' : 'Sync Failed';
            case 'disconnected':
                return dataSource.dataSourceType === 'Excel' ? 'Not Uploaded' : 'Not Connected';
            case 'not-synced':
                return dataSource.dataSourceType === 'Excel' ? 'Not Uploaded' : 'Not Synced';
            default:
                return dataSource.dataSourceType === 'Excel' ? 'Not Uploaded' : 'Not Connected';
        }
    };

    ////////////////////// render functions //////////////////////
    const renderMoreSelection = () => (
        <div className="more-selection-menu">
            {localDataSource.dataSourceType !== 'Excel' &&
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalDataSource({...localDataSource, syncFreq: 'daily'});
                setMoreSelectionOpen(false);
            }}>
                <span>Daily</span>
                {/* <span className="pro-badge">Pro</span> */}
            </div>}
            {localDataSource.dataSourceType !== 'Excel' &&
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalDataSource({...localDataSource, syncFreq: 'weekly'});
                setMoreSelectionOpen(false);
            }}>
                <span>Once a Week</span>
            </div>}
            {localDataSource.dataSourceType !== 'Excel' &&
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalDataSource({...localDataSource, syncFreq: 'monthly'});
                setMoreSelectionOpen(false);
            }}>
                <span>Once a Month</span>
            </div>}
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalDataSource({...localDataSource, syncFreq: 'one-time'});
                setMoreSelectionOpen(false);
            }}>
                <span>One Time</span>
            </div>
        </div>
    );

    const [editedTitle, setEditedTitle] = useState(localDataSource.name);
    const renderTitleDropdown = () => (
        <div className="more-selection-menu">
            <p className="title">Change Title</p>
            <div className="title-change">
                <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder={localDataSource.name || 'New Data Source'}
                    autoFocus
                />
            </div>
            <div className="actions">
                <div className="action cancel" onClick={() => {
                    setTitleDropdownOpen(false);
                    setEditedTitle(localDataSource.name);
                }}>
                    <span>Cancel</span>
                </div>
                <div className="action confirm" onClick={() => {
                    setLocalDataSource({...localDataSource, name: editedTitle});
                    setTitleDropdownOpen(false);
                }}>
                    <span>Confirm</span>
                </div>
            </div>
        </div>
    );

    const renderConfirmation = () => (
        <Popup
            content={
                <div className="confirmation-popup">
                    <div className="confirmation-popup-content">
                        {localDataSource.dataSourceType === 'Excel' ? (
                            <>
                                <h2>Are you sure you want to remove this data?</h2>
                                <p>Removing will delete all associated data from Vairo. You can always re-upload at any time.</p>
                            </>
                        ) : (
                            <>
                                <h2>Are you sure you want to disconnect this data source?</h2>
                                <p>Disconnecting will remove this data source and all associated data from Vairo. You can always reconnect at any time.</p>
                            </>
                        )}
                        <div className="actions">
                            <div className="action cancel" onClick={() => setConfirmationOpen(false)}>
                                <span>Cancel</span>
                            </div>
                            <div className="action confirm" onClick={handleConfirmDisconnectClicked}>
                                {localDataSource.dataSourceType === 'Excel' ? <span>Remove</span> : <span>Disconnect</span>}
                            </div>
                        </div>
                    </div>
                </div>
            }
            onClose={() => setConfirmationOpen(false)}
        >
        </Popup>
    );

    const renderAddSourceModal = () => (
        <Popup
            content={<CreateDataSource setAddDataSourceOpen={setAddDataSourceOpen} setDataSources={setDataSources} updateDataSource={updateDataSource} setAlert={setAlert} initialStep={2} initialDataSource={localDataSource} initialDataSourceType={localDataSource.dataSourceType} />}
            onClose={() => setAddDataSourceOpen(false)}
        >
        </Popup>
    );

    const renderSyncModal = () => (
        <Popup
            content={<CreateDataSource setAddDataSourceOpen={setSyncOpen} setDataSources={setDataSources} updateDataSource={updateDataSource} setAlert={setAlert} initialStep={2} initialAuthStatus={'success'} initialDataSource={localDataSource} initialDataSourceType={localDataSource.dataSourceType} />}
            onClose={() => setSyncOpen(false)}
        >
        </Popup>
    );

    const canResync = localDataSource.dataSourceType !== 'Excel'
        && localDataSource.connectionId
        && localDataSource.status !== 'syncing'
        && localDataSource.status !== 'disconnected'
        && localDataSource.status !== 'not-synced';

    return (
        <div className={`data-source-details-wrapper ${loading ? 'loading' : ''}`}>
            {loading && <LoadingScreen />}
            <div className="section header">
                <img src={getLogo()} alt={localDataSource.name} />
                <div className="header-titles">
                    <div className="title">
                        <h1>{localDataSource.name}</h1>
                        <MoreIcon className="more-icon" onClick={() => setTitleDropdownOpen(!titleDropdownOpen)} />
                        {titleDropdownOpen && renderTitleDropdown()}
                    </div>
                    <div className="subtitle">
                        <h2>{localDataSource.dataSourceType}</h2>
                    </div>
                </div>
                {canResync && (
                    <button
                        className={`resync-button ${resyncing ? 'resyncing' : ''}`}
                        onClick={handleResyncClicked}
                        disabled={resyncing}
                    >
                        <RefreshIcon className="resync-icon" />
                        {resyncing ? 'Syncing...' : 'Resync'}
                    </button>
                )}
            </div>
            <hr className="divider" />
            <div className="section metadata">
                <div className="item status">
                    <h2>Connection Status</h2>
                    <div className={`content indicator ${localDataSource?.status}`}>
                        {localDataSource.status === "syncing" && <div className="syncing-spinner"></div>}
                        {getStatusDisplayText(localDataSource?.status)}
                    </div>
                </div>
                <div className="item last-synced">
                    <h2>Last Synced</h2>

                    <div className="content">{localDataSource.lastSynced ? formatDate(localDataSource.lastSynced) : 'Never'}</div>
                </div>
                <div className="item date-connected">
                    <h2>Date Created</h2>
                    <div className="content">{formatDate(localDataSource.createdAt)}</div>
                </div>
            </div>
            <hr className="divider" />
            <div className="section privacy">
                <div className="item access-type">
                    <div className="text">
                        <h2>Privacy Settings</h2>
                        <p>{localDataSource.accessType === 'team-wide' ? 'Team-wide - visible to your workspace members' : 'Private - visible on an invite-only basis'}</p>
                    </div>
                    <div className={`toggle ${localDataSource.accessType === 'team-wide' ? 'team-wide' : 'private'}`} onClick={handleToggleAccessType} >
                        <div className='slider'></div>
                    </div>
                </div>
            </div>
            <hr className="divider" />
            <div className="section sync">
                <div className="item sync-frequency">
                    <h2>Sync Frequency</h2>
                    <div className="content" onClick={() => setMoreSelectionOpen(!moreSelectionOpen)}>
                        <h3 className="text">{formatSyncFreq()}</h3>
                        <DownIcon className="down-icon" />
                        {moreSelectionOpen && renderMoreSelection()}
                    </div>

                </div>
            </div>
            <div className="section footer">
                <div className="footer-left">
                    <button className={`button disconnect ${localDataSource?.status}`} onClick={() => {
                        if (localDataSource?.status === 'ready') {
                            setConfirmationOpen(true);
                        } else if (localDataSource?.status === 'disconnected') {
                            handleConnectSourceClicked();
                        } else if (localDataSource?.status === 'syncing') {
                            //do nothing
                        } else if (localDataSource?.status === 'sync-failed') {
                            handleBeginSyncClicked();
                        } else if (localDataSource?.status === 'not-synced') {
                            handleBeginSyncClicked();
                        }
                    }}>
                        {getButtonTextByStatus(localDataSource?.status)}
                    </button>
                    {confirmationOpen && renderConfirmation()}
                </div>
                <div className="footer-right">
                    <button className="button cancel" onClick={handleCancelClicked}>Cancel</button>


                    <button className={`button save ${isSaved ? 'saved' : ''}`} onClick={handleSaveClicked}>{isSaved ? 'Saved' : 'Save'}</button>
                </div>
            </div>
            {addDataSourceOpen && renderAddSourceModal()}
            {syncOpen && renderSyncModal()}
        </div>
    );
};

export default DataSourceDetails;
